import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot, Label as LabelPrimitive, Separator as SeparatorPrimitive, Checkbox as CheckboxPrimitive, Switch as SwitchPrimitive, Avatar as AvatarPrimitive } from 'radix-ui';
import { Check, Loader2 } from 'lucide-react';
import { cn } from './cn.js';

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-[var(--radius)] text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0 cursor-pointer',
  {
    variants: {
      variant: {
        primary: 'bg-accent text-accent-fg hover:bg-accent-hover shadow-sm',
        secondary: 'bg-surface text-fg border border-border-strong hover:bg-muted shadow-sm',
        ghost: 'text-fg-muted hover:bg-muted hover:text-fg',
        danger: 'bg-danger text-white hover:opacity-90 shadow-sm',
        link: 'text-accent underline-offset-2 hover:underline px-0',
      },
      size: {
        sm: 'h-7 px-2.5 text-xs',
        md: 'h-8 px-3',
        lg: 'h-10 px-4',
        icon: 'size-8',
        iconSm: 'size-7',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, asChild, loading, children, disabled, ...props }, ref) => {
  const Comp = asChild ? Slot.Root : 'button';
  return (
    <Comp ref={ref} className={cn(buttonVariants({ variant, size }), className)} disabled={disabled || loading} aria-busy={loading || undefined} {...props}>
      {asChild ? (
        children
      ) : (
        <>
          {loading ? <Spinner className="text-current" /> : null}
          {children}
        </>
      )}
    </Comp>
  );
});
Button.displayName = 'Button';

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({ className, ...props }, ref) => (
  <input
    ref={ref}
    className={cn(
      'h-8 w-full rounded-[var(--radius)] border border-border-strong bg-surface px-2.5 text-sm text-fg placeholder:text-fg-subtle shadow-xs outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30 disabled:opacity-60 aria-[invalid=true]:border-danger',
      className,
    )}
    {...props}
  />
));
Input.displayName = 'Input';

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, ...props }, ref) => (
  <textarea
    ref={ref}
    className={cn(
      'min-h-20 w-full rounded-[var(--radius)] border border-border-strong bg-surface px-2.5 py-2 text-sm text-fg placeholder:text-fg-subtle shadow-xs outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30 disabled:opacity-60 aria-[invalid=true]:border-danger',
      className,
    )}
    {...props}
  />
));
Textarea.displayName = 'Textarea';

export const NativeSelect = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(({ className, ...props }, ref) => (
  <select
    ref={ref}
    className={cn(
      'h-8 w-full rounded-[var(--radius)] border border-border-strong bg-surface px-2 text-sm text-fg shadow-xs outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30 disabled:opacity-60',
      className,
    )}
    {...props}
  />
));
NativeSelect.displayName = 'NativeSelect';

export const Label = React.forwardRef<React.ComponentRef<typeof LabelPrimitive.Root>, React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>>(({ className, ...props }, ref) => (
  <LabelPrimitive.Root ref={ref} className={cn('text-xs font-medium text-fg-muted', className)} {...props} />
));
Label.displayName = 'Label';

/**
 * Labeled form field. When no htmlFor is given and the field wraps a single control, the label is
 * associated automatically, and the hint or error is linked through aria-describedby.
 */
export function Field({ label, htmlFor, hint, error, children, className, required }: { label: React.ReactNode; htmlFor?: string; hint?: React.ReactNode; error?: React.ReactNode; children: React.ReactNode; className?: string; required?: boolean }) {
  const autoId = React.useId();
  const single = React.isValidElement<{ id?: string; 'aria-describedby'?: string }>(children) && children.type !== React.Fragment ? children : null;
  const controlId = htmlFor ?? single?.props.id ?? (single ? autoId : undefined);
  const descriptionId = error || hint ? `${autoId}-description` : undefined;
  const control =
    single && !htmlFor
      ? React.cloneElement(single, {
          id: controlId,
          ...(descriptionId ? { 'aria-describedby': [single.props['aria-describedby'], descriptionId].filter(Boolean).join(' ') } : {}),
          ...(error ? { 'aria-invalid': true } : {}),
        } as Record<string, unknown>)
      : children;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={controlId}>
        {label}
        {required ? <span className="text-danger"> *</span> : null}
      </Label>
      {control}
      {error ? (
        <p id={descriptionId} className="text-xs text-danger" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={descriptionId} className="text-xs text-fg-subtle">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export const Separator = ({ className, ...props }: React.ComponentPropsWithoutRef<typeof SeparatorPrimitive.Root>) => (
  <SeparatorPrimitive.Root className={cn('shrink-0 bg-border data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-px', className)} {...props} />
);

export const Checkbox = React.forwardRef<React.ComponentRef<typeof CheckboxPrimitive.Root>, React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn('peer size-4 shrink-0 rounded-[4px] border border-border-strong bg-surface data-[state=checked]:border-accent data-[state=checked]:bg-accent data-[state=checked]:text-accent-fg', className)}
    {...props}
  >
    <CheckboxPrimitive.Indicator className="flex items-center justify-center">
      <Check className="size-3" />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = 'Checkbox';

export const Switch = React.forwardRef<React.ComponentRef<typeof SwitchPrimitive.Root>, React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>>(({ className, ...props }, ref) => (
  <SwitchPrimitive.Root
    ref={ref}
    className={cn('inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border border-transparent bg-border-strong transition-colors data-[state=checked]:bg-accent', className)}
    {...props}
  >
    <SwitchPrimitive.Thumb className="block size-4 rounded-full bg-white shadow transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0.5" />
  </SwitchPrimitive.Root>
));
Switch.displayName = 'Switch';

export const badgeVariants = cva('inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium [&_svg]:size-3', {
  variants: {
    tone: {
      neutral: 'border-border bg-muted text-fg-muted',
      accent: 'border-transparent bg-accent-soft text-accent-soft-fg',
      success: 'border-transparent bg-success-soft text-success',
      warning: 'border-transparent bg-warning-soft text-warning',
      danger: 'border-transparent bg-danger-soft text-danger',
      info: 'border-transparent bg-info-soft text-info',
      outline: 'border-border-strong bg-transparent text-fg-muted',
    },
  },
  defaultVariants: { tone: 'neutral' },
});

export function Badge({ className, tone, ...props }: React.HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants>) {
  return <span className={cn(badgeVariants({ tone }), className)} {...props} />;
}

export function Card({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('rounded-[var(--radius-lg)] border border-border bg-surface shadow-xs', className)} {...props} />;
}

export function CardHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex items-center justify-between gap-2 border-b border-border px-4 py-3', className)} {...props} />;
}

export function CardTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h2 className={cn('text-sm font-semibold text-fg', className)} {...props} />;
}

export function CardBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-4', className)} {...props} />;
}

/** Length of one spinner turn and one skeleton pulse, as in `.vwt-spinner` and Tailwind's `animate-pulse`. */
const SPIN_MS = 1000;
const PULSE_MS = 2000;

/**
 * A negative delay that puts a loader's animation in phase with the page clock. An animation starts at
 * zero whenever its element mounts, so a loader that remounts while data refetches jumps back and
 * looks sped up, and loaders that appear at different times move out of step. In phase, every loader
 * keeps one rhythm.
 */
function usePhase(periodMs: number): string {
  const [delay] = React.useState(() => `-${Math.round(performance.now() % periodMs)}ms`);
  return delay;
}

export function Skeleton({ className, style, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const delay = usePhase(PULSE_MS);
  return <div className={cn('animate-pulse rounded-[var(--radius)] bg-muted', className)} style={{ animationDelay: delay, ...style }} aria-hidden {...props} />;
}

export function Kbd({ className, ...props }: React.HTMLAttributes<HTMLElement>) {
  return <kbd className={cn('inline-flex h-5 min-w-5 items-center justify-center rounded border border-border-strong bg-surface-2 px-1 font-mono text-[11px] text-fg-muted', className)} {...props} />;
}

export function Avatar({ name, className, size = 'md' }: { name: string; className?: string; size?: 'sm' | 'md' | 'lg' }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
  let hash = 0;
  for (const ch of name) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  const hues = [172, 199, 221, 262, 291, 330, 24, 45, 142];
  const hue = hues[hash % hues.length];
  const dims = size === 'sm' ? 'size-5 text-[10px]' : size === 'lg' ? 'size-9 text-sm' : 'size-7 text-xs';
  return (
    <AvatarPrimitive.Root className={cn('inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white select-none', dims, className)} style={{ background: `hsl(${hue} 55% 42%)` }} title={name}>
      <AvatarPrimitive.Fallback>{initials || '?'}</AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );
}

export function EmptyState({ icon, title, description, action, className }: { icon?: React.ReactNode; title: React.ReactNode; description?: React.ReactNode; action?: React.ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-2 px-6 py-12 text-center', className)}>
      {icon ? <div className="mb-1 flex size-10 items-center justify-center rounded-full bg-muted text-fg-subtle [&_svg]:size-5">{icon}</div> : null}
      <p className="text-sm font-medium text-fg">{title}</p>
      {description ? <p className="max-w-sm text-sm text-fg-muted">{description}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

/**
 * The loading indicator. `className` styles the icon (size, color). The wrapper turns instead of the
 * SVG so the browser can animate it off the main thread, and it stays in phase across remounts.
 */
export function Spinner({ className, label }: { className?: string; label?: string }) {
  const delay = usePhase(SPIN_MS);
  return (
    <span className="vwt-spinner" style={{ animationDelay: delay }} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
      <Loader2 className={cn('size-4 text-fg-subtle', className)} aria-hidden />
    </span>
  );
}
